import { Link } from 'react-router-dom';
import { useScrollRevealGroup } from '../../../hooks/useScrollReveal';

const advantages = [
  {
    icon: 'ri-message-3-line',
    title: '24/7 AI Customer Engagement',
    description: 'Never miss a lead. AI responds to enquiries instantly, books appointments, and qualifies prospects — even at 3am on a Sunday.',
    stat: '40%',
    statLabel: 'of enquiries handled automatically',
  },
  {
    icon: 'ri-article-line',
    title: 'Faster Content at Scale',
    description: 'We deploy AI to generate SEO-optimised content 10x faster than traditional agencies — more pages, more rankings, more traffic.',
    stat: '10x',
    statLabel: 'faster content production',
  },
  {
    icon: 'ri-target-line',
    title: 'Smarter Targeting',
    description: 'AI analyses your customer data to identify who is most likely to convert, then focuses your marketing spend exactly where it counts.',
    stat: '2.5x',
    statLabel: 'better conversion rates',
  },
  {
    icon: 'ri-robot-line',
    title: 'Automated Lead Follow-Up',
    description: 'Most leads go cold because no one followed up fast enough. Our AI follows up within seconds — not hours. Speed wins every time.',
    stat: '78%',
    statLabel: 'of leads respond within 5 min',
  },
];

export default function AISection() {
  const groupRef = useScrollRevealGroup();

  return (
    <section id="ai" className="relative pt-24 pb-16 bg-ds-bg overflow-hidden">
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-ds-blue/30 to-transparent" />
      {/* AI visual glow */}
      <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[600px] h-64 bg-ds-blue/8 blur-[100px] rounded-full pointer-events-none" />

      <div
        className="max-w-7xl mx-auto px-6"
        ref={groupRef}
      >
        {/* Top layout — heading + hero image */}
        <div className="grid lg:grid-cols-2 gap-16 items-center mb-20">
          <div>
            <span className="section-label reveal">The AI Advantage</span>
            <h2 className="font-display text-6xl md:text-7xl text-white leading-none mb-6 reveal">
              AI IS THE
              <br />
              <span className="text-ds-blue neon-text-blue">UNFAIR</span>
              <br />
              ADVANTAGE.
            </h2>
            <p className="font-body text-white/60 text-lg leading-relaxed mb-6 reveal">
              Your competitors are still manually responding to emails, paying for every piece 
              of content, and wondering why they&apos;re losing. We give local businesses the exact 
              same AI arsenal that Fortune 500 companies use — at a fraction of the cost.
            </p>
            <p className="font-body text-white/50 text-base leading-relaxed reveal">
              Smarter automation. Stronger presence. A business that works 24/7 even when you don&apos;t. 
              This isn&apos;t the future — this is right now. And most of your competitors haven&apos;t caught up yet.
            </p>
            <div className="mt-8 flex items-center gap-4 reveal">
              <div className="h-px flex-1 bg-ds-blue/20" />
              <span className="font-body text-ds-blue text-xs tracking-widest uppercase">
                The window won&apos;t stay open forever
              </span>
              <div className="h-px flex-1 bg-ds-blue/20" />
            </div>
          </div>

          {/* AI Visual */}
          <div className="reveal-right relative">
            <div className="relative overflow-hidden">
              <img
                src="https://public.readdy.ai/ai/img_res/4b8075286bc3f1f8ff88d2b886b5ffba.jpg"
                alt="AI Neural Network Visualization"
                className="w-full h-80 lg:h-[500px] object-cover object-top"
                loading="lazy"
                decoding="async"
              />
              {/* Overlay */}
              <div className="absolute inset-0 bg-gradient-to-t from-ds-bg via-transparent to-transparent" />
              <div className="absolute inset-0 bg-gradient-to-r from-ds-bg/50 via-transparent to-transparent" />
            </div>

            {/* Floating stat card */}
            <div
              className="absolute bottom-8 right-0 bg-ds-bg-2 border border-ds-blue/30 p-5 w-44"
              style={{ boxShadow: '0 0 30px rgba(0,240,255,0.15)' }}
            >
              <div className="font-display text-4xl text-ds-blue mb-1">98%</div>
              <div className="font-body text-white/60 text-xs leading-tight">AI accuracy rate for customer query resolution</div>
            </div>
          </div>
        </div>

        {/* Advantage cards */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
          {advantages.map((adv, i) => (
            <div
              key={adv.title}
              className="relative bg-ds-bg-2 border border-ds-border p-6 group cursor-pointer reveal overflow-hidden transition-all duration-300 hover:border-ds-blue/40"
              style={{ transitionDelay: `${i * 100}ms` }}
            >
              <div className="absolute inset-0 bg-ds-blue/0 group-hover:bg-ds-blue/3 transition-all duration-500 pointer-events-none" />

              <div className="w-10 h-10 flex items-center justify-center border border-ds-blue/20 group-hover:border-ds-blue/60 mb-4 transition-colors duration-300">
                <i className={`${adv.icon} text-ds-blue text-lg`} />
              </div>
              <h3 className="font-heading font-700 text-white text-base mb-2 leading-tight group-hover:text-ds-blue transition-colors duration-300">
                {adv.title}
              </h3>
              <p className="font-body text-white/50 text-xs leading-relaxed mb-4">
                {adv.description}
              </p>
              <div className="pt-4 border-t border-ds-border">
                <span className="font-display text-2xl text-ds-blue">{adv.stat}</span>
                <p className="font-body text-white/40 text-xs mt-0.5">{adv.statLabel}</p>
              </div>
            </div>
          ))}
        </div>

        {/* GEO/AEO Next Evolution Strip */}
        <div className="mt-6 reveal">
          <Link
            to="/geo-aeo"
            className="group block w-full border border-ds-red/30 bg-ds-bg-2 hover:border-ds-red/60 transition-all duration-300"
            style={{ boxShadow: '0 0 40px rgba(180,255,57,0.06)' }}
          >
            <div className="grid lg:grid-cols-12 items-stretch">

              {/* Left label tab */}
              <div className="lg:col-span-2 bg-ds-red/8 border-r border-ds-red/20 flex items-center justify-center px-6 py-6 gap-3">
                <span className="relative flex h-2 w-2 flex-shrink-0">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-ds-red opacity-60" />
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-ds-red" />
                </span>
                <span className="font-display text-ds-red text-xs tracking-[0.3em] uppercase whitespace-nowrap">
                  What&apos;s Next
                </span>
              </div>

              {/* Centre — copy */}
              <div className="lg:col-span-8 px-8 py-6 flex flex-col justify-center border-r border-ds-border/50">
                <p className="font-display text-white text-base md:text-lg leading-tight mb-1">
                  AI automation is just the start.{' '}
                  <span className="text-ds-red">The next frontier is AI search.</span>
                </p>
                <p className="font-body text-white/50 text-sm leading-relaxed">
                  While your competitors race to automate, the real opportunity is becoming the business ChatGPT, Gemini, and Perplexity <em>recommend by name</em> when customers ask who to call. That&apos;s GEO / AEO — and 47% of businesses have zero strategy for it yet.
                </p>
              </div>

              {/* Right CTA */}
              <div className="lg:col-span-2 flex items-center justify-center px-6 py-6">
                <div className="flex items-center gap-2 text-ds-red group-hover:gap-3 transition-all duration-300">
                  <span className="font-display text-xs tracking-widest uppercase whitespace-nowrap">Explore GEO</span>
                  <div className="w-5 h-5 flex items-center justify-center border border-ds-red/40 group-hover:border-ds-red group-hover:bg-ds-red/10 transition-all duration-300">
                    <i className="ri-arrow-right-line text-xs" />
                  </div>
                </div>
              </div>

            </div>
          </Link>
        </div>
      </div>
    </section>
  );
}
